import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { IssuePriority, IssueType } from "../components/management/board/types";
import { KMainFunctions } from "../shared/utility/main-functions";

// REST client for the board AI assist layer (CoreApi BoardAiController, v1/api/board/ai).
// Every endpoint is suggestion-only — it returns a proposal the UI shows for review; applying
// it goes through the normal BoardApi mutations. CoreApi serialises camelCase, so no casing
// translation is needed. API calls live here (never inline in components).

const aiUrl: () => string = (): string => `${CommonHelperFunctions.GetBackendUrl()}/board/ai`;

async function get<T>(path: string): Promise<T> {
  try {
    const response: AxiosResponse<T> = await Axios.get<T, AxiosResponse<T>>(`${aiUrl()}${path}`);
    return response.data;
  } catch (error: unknown) {
    KMainFunctions.exceptionHandler(error);
    throw error;
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  try {
    const response: AxiosResponse<T> = await Axios.post<T, AxiosResponse<T>>(`${aiUrl()}${path}`, body ?? {});
    return response.data;
  } catch (error: unknown) {
    KMainFunctions.exceptionHandler(error);
    throw error;
  }
}

// ── Wire shapes (camelCase, mirror Backend BoardAiModels) ──────────────────

export interface BoardAiCapabilities {
  enabled: boolean;
}

export interface BoardAiTriageResult {
  type: IssueType;
  priority: IssuePriority;
  labels: string[];
  storyPoints: number | null;
  summary: string;
  descriptionMarkdown: string;
}

export interface BoardAiJqlResult {
  jql: string;
  explanation: string;
}

// Loosely typed (vs the frontend's discriminated unions) because it's a parse of model output;
// the automation modal maps it onto its typed form state by kind.
export interface BoardAiAutomationTrigger {
  kind: string;
  statusId?: string | null;
  priority?: string | null;
}

export interface BoardAiAutomationAction {
  kind: string;
  label?: string | null;
  body?: string | null;
  priority?: string | null;
  statusId?: string | null;
}

export interface BoardAiAutomationResult {
  name: string;
  trigger: BoardAiAutomationTrigger | null;
  action: BoardAiAutomationAction | null;
  error: string | null;
}

export type BoardAiCommentMode = "draft" | "rewrite" | "reply" | "translate";

export interface BoardAiCommentAssistRequest {
  mode: BoardAiCommentMode;
  text?: string;
  issueId?: string;
  tone?: string;
  targetLanguage?: string;
}

export interface BoardAiTextResult {
  text: string;
}

export interface BoardAiSubtask {
  summary: string;
}

export interface BoardAiSubtasksResult {
  subtasks: BoardAiSubtask[];
}

export interface BoardAiDescribeResult {
  descriptionMarkdown: string;
}

export interface BoardAiDuplicateCandidate {
  issueId: string;
  key: string;
  summary: string;
  reason: string;
  confidence: string;
}

export interface BoardAiDuplicatesResult {
  candidates: BoardAiDuplicateCandidate[];
}

export interface BoardAiSimilarIssue {
  key: string;
  summary: string;
  storyPoints: number | null;
}

export interface BoardAiEstimateResult {
  storyPoints: number | null;
  rationale: string;
  similar: BoardAiSimilarIssue[];
}

export interface BoardAiAskSource {
  key: string;
  summary: string;
}

export interface BoardAiAskResult {
  answer: string;
  sources: BoardAiAskSource[];
}

export interface BoardAiMarkdownResult {
  markdown: string;
  count: number;
}

// ── Client ─────────────────────────────────────────────────────────────────

export class BoardAiApi {
  static async getCapabilities(): Promise<BoardAiCapabilities> {
    return get<BoardAiCapabilities>("/capabilities");
  }

  static async triage(summary: string, description?: string): Promise<BoardAiTriageResult> {
    return post<BoardAiTriageResult>("/triage", { summary, description });
  }

  static async toJql(query: string): Promise<BoardAiJqlResult> {
    return post<BoardAiJqlResult>("/jql", { query });
  }

  static async toAutomationRule(instruction: string): Promise<BoardAiAutomationResult> {
    return post<BoardAiAutomationResult>("/automation-rule", { instruction });
  }

  static async commentAssist(request: BoardAiCommentAssistRequest): Promise<BoardAiTextResult> {
    return post<BoardAiTextResult>("/comment-assist", request);
  }

  static async suggestSubtasks(issueId: string): Promise<BoardAiSubtasksResult> {
    return post<BoardAiSubtasksResult>("/subtasks", { issueId });
  }

  static async describe(input: { issueId?: string; summary?: string; type?: string }): Promise<BoardAiDescribeResult> {
    return post<BoardAiDescribeResult>("/describe", input);
  }

  static async findDuplicates(input: {
    summary: string;
    description?: string;
    excludeIssueId?: string;
  }): Promise<BoardAiDuplicatesResult> {
    return post<BoardAiDuplicatesResult>("/duplicates", input);
  }

  static async estimate(input: {
    summary?: string;
    description?: string;
    issueId?: string;
  }): Promise<BoardAiEstimateResult> {
    return post<BoardAiEstimateResult>("/estimate", input);
  }

  static async summarizeIssue(issueId: string): Promise<BoardAiTextResult> {
    return post<BoardAiTextResult>("/summarize", { issueId });
  }

  static async ask(question: string): Promise<BoardAiAskResult> {
    return post<BoardAiAskResult>("/ask", { question });
  }

  static async releaseNotes(input: {
    versionId?: string;
    sprintId?: string;
    projectKey?: string;
  }): Promise<BoardAiMarkdownResult> {
    return post<BoardAiMarkdownResult>("/release-notes", input);
  }

  static async digest(): Promise<BoardAiMarkdownResult> {
    return get<BoardAiMarkdownResult>("/digest");
  }
}
